(() => {
  if (window.nebulineHomeExperience) {
    window.nebulineHomeExperience.initialize(document);
    return;
  }
  const instances = new Map();
  let transitionCount = 0;
  const homesIn = (root) => [
    ...(root.matches?.('[data-nebuline-home]') ? [root] : []),
    ...root.querySelectorAll('[data-nebuline-home]'),
  ];
  const initialize = (root = document) => {
    homesIn(root).forEach((home) => {
      if (instances.has(home)) return;
      const controller = new AbortController();
      const { signal } = controller;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      const animations = new Set();
      const cleanups = [];
      const on = (element, event, handler, options = {}) => element?.addEventListener(event, handler, { ...options, signal });
      const animate = (element, frames, options = {}) => {
        if (motion.matches || !element?.animate) return null;
        const animation = element.animate(frames, { duration: 550, easing: 'cubic-bezier(.2,.7,.2,1)', ...options });
        animations.add(animation);
        animation.finished.catch(() => {}).finally(() => animations.delete(animation));
        return animation;
      };
      home.dataset.nbInitialized = 'true';

      home.querySelectorAll('[data-nb-tabs]').forEach((group, index) => {
        const tabs = Array.from(group.querySelectorAll('[role="tab"]'));
        const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
        const transitionName = `nb-style-${home.dataset.sectionId}-${index}`.replace(/[^a-z0-9-]/gi, '-');
        let transition;
        let selection = 0;
        group.dataset.nbTabsReady = 'true';
        const tablist = group.querySelector('[data-nb-tablist]');
        if (tablist) tablist.hidden = false;
        const initialIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
        tabs.forEach((tab, tabIndex) => {
          tab.setAttribute('aria-selected', String(tabIndex === initialIndex));
          tab.tabIndex = tabIndex === initialIndex ? 0 : -1;
        });
        panels.forEach((panel, panelIndex) => { if (panel) panel.hidden = panelIndex !== initialIndex; });
        const activate = (tab, focus) => {
          const alreadySelected = tab.getAttribute('aria-selected') === 'true';
          if (focus) tab.focus({ preventScroll: true });
          if (alreadySelected) return;
          const currentSelection = ++selection;
          transition?.skipTransition();
          tabs.forEach((item) => {
            const selected = item === tab;
            item.setAttribute('aria-selected', String(selected));
            item.tabIndex = selected ? 0 : -1;
          });
          const target = document.getElementById(tab.getAttribute('aria-controls'));
          const updatePanels = () => {
            if (selection !== currentSelection || signal.aborted) return;
            panels.forEach((panel) => { if (panel) panel.hidden = panel !== target; });
          };
          if (!motion.matches && document.startViewTransition && !window.Shopify?.designMode) {
            panels.forEach((panel) => { if (panel) panel.style.viewTransitionName = transitionName; });
            transitionCount += 1;
            document.documentElement.classList.add('nb-home-tab-transition');
            try {
              const active = document.startViewTransition(updatePanels);
              transition = active;
              active.ready.catch(() => {});
              active.updateCallbackDone.catch(() => {});
              active.finished.catch(() => {}).finally(() => {
                transitionCount = Math.max(0, transitionCount - 1);
                if (transitionCount === 0) document.documentElement.classList.remove('nb-home-tab-transition');
                if (transition === active) {
                  transition = undefined;
                  panels.forEach((panel) => { if (panel) panel.style.removeProperty('view-transition-name'); });
                }
              });
            } catch {
              transitionCount = Math.max(0, transitionCount - 1);
              if (transitionCount === 0) document.documentElement.classList.remove('nb-home-tab-transition');
              panels.forEach((panel) => panel?.style.removeProperty('view-transition-name'));
              updatePanels();
            }
          } else {
            updatePanels();
            animate(target, [{ opacity: .4, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 300 });
          }
        };
        tabs.forEach((tab, index) => {
          on(tab, 'click', () => activate(tab, false));
          on(tab, 'keydown', (event) => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next !== undefined) {
              event.preventDefault();
              activate(tabs[next], true);
            }
          });
        });
        cleanups.push(() => transition?.skipTransition());
        on(motion, 'change', () => { if (motion.matches) transition?.skipTransition(); });
      });

      home.querySelectorAll('[data-nb-product]').forEach((product) => {
        const selector = product.querySelector('[data-nb-variant]');
        on(selector, 'change', () => {
          const option = selector.selectedOptions[0];
          const button = product.querySelector('[data-nb-add]');
          const label = product.querySelector('[data-nb-add-label]');
          const price = product.querySelector('[data-nb-price]');
          if (!option || !button) return;
          const available = option.dataset.available === 'true';
          button.disabled = !available;
          button.removeAttribute('aria-disabled');
          const component = product.querySelector('product-form');
          if (component) {
            component.handleErrorMessage?.();
            component.error = false;
          }
          if (label) label.textContent = available ? button.dataset.availableLabel : button.dataset.unavailableLabel;
          if (price) price.textContent = option.dataset.price;
        });
      });

      const zoomTrigger = home.querySelector('[data-nb-zoom-open]');
      const zoomDialog = home.querySelector('[data-nb-zoom-dialog]');
      if (zoomDialog?.showModal && zoomTrigger) {
        let returnFocus;
        zoomTrigger.hidden = false;
        const afterClose = () => {
          if (!document.querySelector('[data-nb-zoom-dialog][open]')) document.body.classList.remove('nb-home-zoom-open');
          if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
        };
        on(zoomTrigger, 'click', () => {
          if (zoomDialog.open) return;
          returnFocus = zoomTrigger;
          zoomDialog.showModal();
          document.body.classList.add('nb-home-zoom-open');
          animate(zoomDialog, [{ opacity: .4, transform: 'translateY(16px) scale(.98)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }], { duration: 260 });
        });
        on(zoomDialog.querySelector('[data-nb-zoom-close]'), 'click', () => zoomDialog.close());
        on(zoomDialog, 'close', afterClose);
        on(zoomDialog, 'keydown', (event) => {
          if (event.key !== 'Tab') return;
          const focusable = Array.from(zoomDialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')).filter((element) => element.getClientRects().length > 0);
          if (focusable.length === 0) {
            event.preventDefault();
            zoomDialog.focus();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          const outside = !zoomDialog.contains(document.activeElement) || document.activeElement === zoomDialog;
          if (event.shiftKey && (document.activeElement === first || outside)) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && (document.activeElement === last || outside)) {
            event.preventDefault();
            first.focus();
          }
        });
        on(zoomDialog, 'click', (event) => {
          if (event.target !== zoomDialog) return;
          const bounds = zoomDialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) zoomDialog.close();
        });
        cleanups.push(() => {
          zoomTrigger.hidden = true;
          if (zoomDialog.open) zoomDialog.close();
          afterClose();
        });
      }

      const nativeDetailsAnimation = CSS.supports('interpolate-size: allow-keywords') && CSS.supports('selector(details::details-content)');
      if (!nativeDetailsAnimation) {
        home.querySelectorAll('.nb-home-faq-list details').forEach((details) => {
          on(details, 'toggle', () => {
            if (details.open) animate(details.querySelector('summary + div'), [{ opacity: .3, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 220 });
          });
        });
      }

      const revealTargets = home.querySelectorAll('.nb-home-hero-copy > .nb-home-eyebrow, .nb-home-hero-copy > h1, .nb-home-hero-intro, .nb-home-portrait-image, .nb-home-story-heading, .nb-home-story-chapter > h3, .nb-home-story-chapter > p, .nb-home-product-copy > h2, .nb-home-product-intro, .nb-home-styles-heading, .nb-home-editorial-copy > h2, .nb-home-editorial-copy > p, .nb-home-faq-heading, .nb-home-closing p');
      const reveal = (element) => {
        if (element.dataset.nbRevealed) return;
        element.dataset.nbRevealed = 'true';
        animate(element, [{ opacity: .45, transform: 'translateY(24px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: element.matches('h1') ? 750 : 580 });
      };
      if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            observer.unobserve(entry.target);
          });
        }, { threshold: .12, rootMargin: '0px 0px -24px 0px' });
        revealTargets.forEach((element) => {
          element.dataset.nbReveal = '';
          observer.observe(element);
        });
        cleanups.push(() => observer.disconnect());
      }

      const portrait = home.querySelector('.nb-home-portrait');
      const portraitImage = portrait?.querySelector('img');
      let depthFrame;
      let depthX = 0;
      let depthY = 0;
      const resetDepth = () => {
        cancelAnimationFrame(depthFrame);
        depthFrame = undefined;
        portrait?.classList.remove('nb-home-depth-active');
        portraitImage?.style.removeProperty('--nb-depth-x');
        portraitImage?.style.removeProperty('--nb-depth-y');
      };
      on(portrait, 'pointermove', (event) => {
        if (motion.matches || !finePointer.matches || event.pointerType === 'touch') return;
        const bounds = portrait.getBoundingClientRect();
        depthX = (event.clientX - bounds.left) / bounds.width - .5;
        depthY = (event.clientY - bounds.top) / bounds.height - .5;
        if (depthFrame) return;
        depthFrame = requestAnimationFrame(() => {
          portrait.classList.add('nb-home-depth-active');
          portraitImage.style.setProperty('--nb-depth-x', `${(depthX * 9).toFixed(2)}px`);
          portraitImage.style.setProperty('--nb-depth-y', `${(depthY * 9).toFixed(2)}px`);
          depthFrame = undefined;
        });
      }, { passive: true });
      on(portrait, 'pointerleave', resetDepth);
      on(finePointer, 'change', resetDepth);
      on(motion, 'change', () => {
        resetDepth();
        if (motion.matches) animations.forEach((animation) => animation.cancel());
      });
      cleanups.push(resetDepth);
      const story = home.querySelector('[data-nb-story]');
      if (story) {
        const chapters = Array.from(story.querySelectorAll('[data-nb-chapter]'));
        const counter = story.querySelector('[data-nb-story-count]');
        const desktopStory = matchMedia('(min-width: 901px)');
        let storyFrame;
        let storyVisible = !('IntersectionObserver' in window);
        const updateStory = () => {
          storyFrame = undefined;
          if (motion.matches || !desktopStory.matches || window.Shopify?.designMode) {
            story.style.removeProperty('--nb-story-progress');
            story.removeAttribute('data-nb-story-phase');
            if (counter) counter.textContent = '01';
            return;
          }
          const bounds = story.getBoundingClientRect();
          const viewport = window.innerHeight;
          const progress = Math.min(1, Math.max(0, (viewport * .2 - bounds.top) / Math.max(1, bounds.height - viewport)));
          let chapterIndex = 0;
          chapters.forEach((chapter, index) => {
            if (chapter.getBoundingClientRect().top < viewport * .6) chapterIndex = index;
          });
          story.style.setProperty('--nb-story-progress', progress.toFixed(3));
          story.dataset.nbStoryPhase = String(chapterIndex);
          if (counter) counter.textContent = String(chapterIndex + 1).padStart(2, '0');
        };
        const scheduleStory = () => {
          if (!storyVisible || storyFrame) return;
          storyFrame = requestAnimationFrame(updateStory);
        };
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(([entry]) => {
            storyVisible = entry.isIntersecting;
            if (storyVisible) scheduleStory();
          }, { rootMargin: '160px 0px', threshold: 0 });
          observer.observe(story);
          cleanups.push(() => observer.disconnect());
        }
        on(window, 'scroll', scheduleStory, { passive: true });
        on(window, 'resize', scheduleStory, { passive: true });
        on(motion, 'change', updateStory);
        on(desktopStory, 'change', updateStory);
        scheduleStory();
        cleanups.push(() => {
          cancelAnimationFrame(storyFrame);
          story.style.removeProperty('--nb-story-progress');
          story.removeAttribute('data-nb-story-phase');
        });
      }
      instances.set(home, () => {
        controller.abort();
        animations.forEach((animation) => animation.cancel());
        cleanups.forEach((cleanup) => cleanup());
        delete home.dataset.nbInitialized;
        instances.delete(home);
      });
    });
  };
  window.nebulineHomeExperience = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => homesIn(event.target).forEach((home) => instances.get(home)?.()));
})();
